import { navigate } from 'gatsby';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';

import { createAutocomplete } from '@algolia/autocomplete-core';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { getAlgoliaResults } from '@algolia/autocomplete-preset-algolia';
import classnames from 'classnames';
import { searchClient } from '../../hooks/useAlgolia';
import useWindowSize from '../../hooks/useWindowSize';
import { ALL_DOCS, SIDEBAR_TUTORIALS } from '../../utils/constants';
import { IconClock, IconEnter, IconNavigationCloseNormal, IconNavigationSearchNormal } from '../Icons';
import { TooltipTrigger } from '../TooltipTrigger/TooltipTrigger';
import './SearchBar.scss';
import SearchBarHits from './SearchBarHits';

export const LIMIT_RECENT_RESEARCH_PANEL = 2;
export const LIMIT_HITS_PANEL = 7;
export const LIMIT_HARDWARE_HITS_PANEL = 3;
export const LIMIT_FILES_HITS_PANEL = 6;

interface SearchBarProps {
  onReset: (state: any) => any;
  onSubmit: (state: any) => any;
  searchState: any;
  actualPage: any;
}

export default function Searchbar({ onReset, onSubmit, searchState, actualPage }: SearchBarProps): React.ReactElement {
  const [selectedPage, setSelectedPage] = useState(ALL_DOCS);
  const [recentSearchPanelFirstCheck, setRecentSearchPanelFirstCheck] = useState(false);

  const [hasResource, setHasResource] = useState(null);

  // Recent Search
  const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: 'RECENT_SEARCH',
    limit: LIMIT_RECENT_RESEARCH_PANEL + 1,
  });

  // (1) Create a React state.
  const [autocompleteState, setAutocompleteState] = React.useState(searchState);
  const [showDetachedPanel, setShowDetachedPanel] = React.useState(false);

  const [limitHitsPanel, setLimitHitsPanel] = useState(LIMIT_HITS_PANEL);
  const [limitHardwareHitsPanel, setLimitHardwareHitsPanel] = useState(LIMIT_HARDWARE_HITS_PANEL);
  const [limitFilesHitsPanel, setLimitFilesHitsPanel] = useState(LIMIT_FILES_HITS_PANEL);

  const [selectedRecentPanelItem, setSelectedRecentPanelItem] = useState(0);
  const [selectedRecentPanelSlug, setSelectedRecentPanelSlug] = useState(null);

  const [selectedPanelItem, setSelectedPanelItem] = useState(0);
  const [selectedPanelSlug, setSelectedPanelSlug] = useState(null);
  const [selectedPanelResourceSlug, setSelectedPanelResourceSlug] = useState(null);

  const windowSize = useWindowSize();
  useEffect(() => {
    if (!windowSize.isDesktop && !showDetachedPanel && autocompleteState.isOpen) {
      setShowDetachedPanel(true);
    } else if (windowSize.isDesktop && showDetachedPanel) {
      setShowDetachedPanel(false);
    }

    if (autocompleteState.isOpen) {
      document.getElementsByTagName('html')[0].classList.add('stop-scrolling');
    } else if (!autocompleteState.isOpen) {
      document.getElementsByTagName('html')[0].classList.remove('stop-scrolling');
    }
  }, [autocompleteState.isOpen, showDetachedPanel, windowSize.isDesktop]);

  const autocomplete = React.useMemo(
    () =>
      createAutocomplete({
        onStateChange({ prevState, state }) {
          // (2) Synchronize the Autocomplete state with the React state.
          if (prevState.query !== state.query) {
            const servicesValue = _.throttle(() => {
              onSubmit({ state: { ...autocompleteState, collections: state.collections, query: state.query, isOpen: true } });
              setSelectedPanelItem(0);
            }, 5000);
            servicesValue();
          }
          setAutocompleteState({ ...state, isOpen: state.query !== '' ? true : state.isOpen });
        },
        getSources(): any {
          return [
            // (3) Use an Algolia index source.
            {
              sourceId: 'tutorials',
              getItemInputValue({ item }) {
                return item.query;
              },
              getItems({ query }) {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: 'globalsearch_query_suggestions',
                      query,
                      params: {
                        //hitsPerPage: 4,
                        hitsPerPage: 4,
                        highlightPreTag: '<mark>',
                        highlightPostTag: '</mark>',
                      },
                    },
                  ],
                });
              },
              getItemUrl({ item }) {
                return item.url;
              },
            },
            {
              sourceId: 'recentSearchesPlugin',
              getItemInputValue({ item }) {
                return item.query;
              },
              getItems({ query }) {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: 'globalsearch_query_suggestions',
                      query,
                      params: {
                        hitsPerPage: 4,
                        highlightPreTag: '<mark>',
                        highlightPostTag: '</mark>',
                      },
                    },
                  ],
                });
              },
              onSelect: function (event) {
                event.setQuery(event.item.name);
              },
              getItemUrl({ item }) {
                return item.url;
              },
            },
          ];
        },
        openOnFocus: true,
        plugins: [recentSearchesPlugin],
        onSubmit: (params) => {
          setShowDetachedPanel(false);
          onSubmit(params);
        },
        onReset: (params) => {
          setShowDetachedPanel(false);
          onReset(params);
        },
        initialState: searchState,
        placeholder: 'Search on Docs',
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onReset, onSubmit, searchState]
  );

  function saveRecentSearch(value: any) {
    let found = false;
    (recentSearchesPlugin.data.getAll() as []).forEach((item: any) => {
      if (item.id === value.objectID) {
        found = true;
      }
    });
    if (!found) recentSearchesPlugin.data.addItem({ id: value.objectID, label: autocompleteState.query });
  }

  function closeAndReset() {
    autocomplete.setIsOpen(false);
    autocomplete.setQuery('');
    setShowDetachedPanel(false);
    setSelectedPanelItem(0);
  }

  // Press / to open searchbar
  // Press arrow up (ArrowUp) or arrow down (ArrowDown) to move between panel list.
  // Press Enter to search.
  // Press Esc for close panel
  if (typeof window !== 'undefined') {
    document.onkeydown = function (event: any) {
      event = event || window.event;
      if (event.key === '/' || (event.key === '/' && event.shiftKey)) {
        setTimeout(() => {
          autocomplete.refresh();
          inputRef.current.focus();
        }, 0); // Avoid to write "/" in the input
      } else if (event.key === 'ArrowUp' && autocompleteState.isOpen) {
        if (!recentSearchPanelFirstCheck) setRecentSearchPanelFirstCheck(true);
        setSelectedRecentPanelItem(selectedRecentPanelItem === 0 ? LIMIT_RECENT_RESEARCH_PANEL : selectedRecentPanelItem - 1);
        const totalHits = limitHitsPanel + limitHardwareHitsPanel + limitFilesHitsPanel;
        if (selectedPanelItem > 0) {
          setSelectedPanelItem(selectedPanelItem - 1);
        } else {
          setSelectedPanelItem(totalHits - 1);
        }
        onSubmit({ state: { ...autocompleteState, collections: autocompleteState.collections, query: autocompleteState.query, isOpen: true } });
      } else if (event.key === 'ArrowDown' && autocompleteState.isOpen) {
        if (!recentSearchPanelFirstCheck) setRecentSearchPanelFirstCheck(true);
        setSelectedRecentPanelItem(selectedRecentPanelItem === LIMIT_RECENT_RESEARCH_PANEL ? 0 : selectedRecentPanelItem + 1);
        const totalHits = limitHitsPanel + limitHardwareHitsPanel + limitFilesHitsPanel;
        if (selectedPanelItem < totalHits - 1) {
          setSelectedPanelItem(selectedPanelItem + 1);
        } else {
          setSelectedPanelItem(0);
        }
        onSubmit({ state: { ...autocompleteState, collections: autocompleteState.collections, query: autocompleteState.query, isOpen: true } });
      } else if (event.key === 'Enter' && autocompleteState.isOpen) {
        if (!recentSearchPanelFirstCheck) setRecentSearchPanelFirstCheck(true);
        if (autocompleteState.query === '' || !autocompleteState.query) {
          autocomplete.setQuery(selectedRecentPanelSlug);
          autocomplete.setIsOpen(true);
          setSelectedPanelItem(0);
          onSubmit({ state: { ...autocompleteState, query: selectedRecentPanelSlug, isOpen: true } }); // Search value
          setTimeout(() => {
            autocomplete.setIsOpen(true);
          }, 0);
        }
        if (autocompleteState.query && autocompleteState.query !== '') {
          if (selectedPanelResourceSlug) {
            window.open(selectedPanelResourceSlug, '_blank').focus();
          } else if (selectedPanelSlug) {
            navigate(selectedPanelSlug);
          }
        }
      } else if (event.key === 'Escape') {
        closeAndReset();
      }
    };
  }

  // Automatic scroll for panel
  useEffect(() => {
    document.querySelector('.search-hits--selected')?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    if (
      document.querySelector('.search-hits--selected') &&
      selectedPanelSlug !== document.querySelector('.search-hits--selected').getAttribute('href')
    ) {
      setSelectedPanelResourceSlug(document.querySelector('.search-hits--selected').getAttribute('href'));
    } else {
      setSelectedPanelResourceSlug(null);
    }
  }, [selectedPanelItem, hasResource, selectedPanelSlug]);

  const inputRef = React.useRef(null);
  const formRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const { getEnvironmentProps } = autocomplete;

  React.useEffect(() => {
    if (!formRef.current || !panelRef.current || !inputRef.current) {
      return undefined;
    }

    const { onTouchStart, onTouchMove } = getEnvironmentProps({
      formElement: formRef.current,
      inputElement: inputRef.current,
      panelElement: panelRef.current,
    });

    window.addEventListener('touchstart', onTouchStart);
    window.addEventListener('touchmove', onTouchMove);

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [getEnvironmentProps, formRef, inputRef, panelRef]);

  const form = (
    <form
      ref={formRef}
      {...(autocomplete.getFormProps({ inputElement: inputRef.current }) as any)}
      className={classnames({ open: autocompleteState && autocompleteState.isOpen })}
    >
      {autocompleteState.isOpen && (
        <div className="header__overlay" onClick={() => closeAndReset()}>
          {' '}
        </div>
      )}
      <div className="header__input-wrapper-prefix">
        <label {...autocomplete.getLabelProps({})}>
          <button title="Search" type="submit">
            <IconNavigationSearchNormal />
          </button>
        </label>
      </div>
      <div className="header__input-wrapper">
        <input
          ref={inputRef}
          {...(autocomplete.getInputProps({} as any) as any)}
          enterKeyHint={'done'}
          onFocus={() => {
            if (!windowSize.isDesktop && !showDetachedPanel) {
              setShowDetachedPanel(true);
            }
            autocomplete.refresh();
            setTimeout(() => {
              autocomplete.setIsOpen(true);
              inputRef.current.focus();
            }, 200);
          }}
        />
      </div>
      <div className="header__input-wrapper-suffix">
        {!(autocompleteState && autocompleteState.isOpen) && (
          <TooltipTrigger
            TriggerElement={<button className="search__key-function">/</button>}
            delay={0}
            tooltipPlacement="bottom"
            tooltipStrategy="absolute"
            tooltipType="tooltip"
          >
            Press “/” to search
          </TooltipTrigger>
        )}
        {autocompleteState && autocompleteState.isOpen && autocomplete.getInputProps({} as any).value !== '' && (
          <>
            <button
              title="Clear"
              type="reset"
              className="search__clear"
              onClick={() => {
                autocomplete.setQuery('');
                autocomplete.refresh();
              }}
            >
              Clear
            </button>
            <span className="search__separator"> | </span>
          </>
        )}
        {autocompleteState.isOpen && (
          <button title="Close" type="reset" className="search__close" onClick={() => closeAndReset()}>
            <IconNavigationCloseNormal />
          </button>
        )}
      </div>
    </form>
  );

  const recentSearchPanel = (
    <div className="header__panel recent-search" {...(autocomplete.getPanelProps({}) as any)}>
      {autocompleteState && autocompleteState.isOpen && (
        <div ref={panelRef} className="header__panel-layout">
          {autocompleteState.collections.map((collection, index) => {
            if (collection.source.sourceId !== 'recentSearchesPlugin') return null;
            const { source, items } = collection;
            return (
              <div key={`source-${index}`} className="header__recent-search">
                {items.length > 0 && (
                  <ul {...autocomplete.getListProps()}>
                    <h5>Recent searches</h5>
                    {items.length === 0 && <span className="header__recent-search--no-tutorial">You haven’t searched yet.</span>}
                    {items.map((item, i) => {
                      if (!item.label) return null;
                      if (selectedRecentPanelItem === i && selectedRecentPanelSlug !== item.label) {
                        setSelectedRecentPanelSlug(item.label);
                      }
                      return (
                        <div
                          key={item.id}
                          className={classnames('header__recent-search__item', {
                            'header__recent-search__item--selected': selectedRecentPanelItem === i,
                            'header__recent-search__item--selected-first-click': !recentSearchPanelFirstCheck,
                          })}
                        >
                          <li
                            {...(autocomplete.getItemProps({
                              item,
                              source,
                            }) as any)}
                            onClick={() => {
                              autocomplete.setQuery(item.label);
                              autocomplete.setIsOpen(true);
                              onSubmit({ state: { ...autocompleteState, query: item.label, isOpen: true } }); // Search value
                            }}
                          >
                            <div className="header__recent-search__box-icon">
                              <span className="header__recent-search__icon">
                                <IconClock />
                              </span>
                              <span>{item.label}</span>
                            </div>
                            {recentSearchPanelFirstCheck && selectedRecentPanelItem === i && !windowSize.isMobile && (
                              <span>
                                <IconEnter />
                              </span>
                            )}
                          </li>
                          {/* Logic to remove recent items - disabled for now due to design decision */}
                          {/* {selectedRecentPanelItem === i && (
                            <button
                              className="header__recent-search__close-button"
                              onClick={() => {
                                recentSearchesPlugin.data.removeItem(item.id);
                                autocomplete.refresh();
                              }}
                            >
                              <IconEnter />
                            </button>
                          )} */}
                        </div>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const panel = (
    <div className="header__panel" {...(autocomplete.getPanelProps({}) as any)}>
      {autocompleteState && autocompleteState.isOpen && (
        <div ref={panelRef} className="header__panel-layout">
          {actualPage.title !== ALL_DOCS && actualPage.title !== SIDEBAR_TUTORIALS && (
            <div className="header__tabs">
              <button
                className={classnames('header__tabs-item', { 'header__tabs-item--selected': selectedPage === ALL_DOCS })}
                onClick={() => {
                  setSelectedPage(ALL_DOCS);
                }}
              >
                On Docs
              </button>
              <button
                className={classnames('header__tabs-item', { 'header__tabs-item--selected': actualPage.title === selectedPage })}
                onClick={() => {
                  setSelectedPage(actualPage.title);
                }}
              >
                In {actualPage.title}
              </button>
            </div>
          )}
          <div className="header__hits">
            <SearchBarHits
              actualPage={actualPage}
              selectedPage={selectedPage}
              setSelectedPanelSlug={setSelectedPanelSlug}
              saveRecentSearch={saveRecentSearch}
              selectedPanelItem={selectedPanelItem}
              setHasResource={setHasResource}
              hasResource={hasResource}
              setLimitHitsPanel={setLimitHitsPanel}
              limitHitsPanel={limitHitsPanel}
              setLimitHardwareHitsPanel={setLimitHardwareHitsPanel}
              limitHardwareHitsPanel={limitHardwareHitsPanel}
              setLimitFilesHitsPanel={setLimitFilesHitsPanel}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="header__autocomplete-wrapper" {...autocomplete.getRootProps({})}>
        {!showDetachedPanel && form}
        {autocompleteState.query === '' ? (recentSearchPanel as any) : (panel as any)}
      </div>
      {showDetachedPanel && ( // Mobile
        <div className="header__detachedOverlay">
          <div className="header__detachedContainer">
            <div className="header__detachedFormContainer">{form}</div>
            {autocompleteState.query === '' ? (recentSearchPanel as any) : (panel as any)}
          </div>
        </div>
      )}
    </>
  );
}
